import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './auth.service.js';

export interface PhotoUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export class PhotoUploadService {
  private storage = storage;

  async uploadPhoto(file: File, assetId: string, companyId: string = 'default'): Promise<PhotoUploadResult> {
    try {
      // Validate file
      if (!file) {
        return {
          success: false,
          error: 'No file provided'
        };
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        return {
          success: false,
          error: 'File must be an image'
        };
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        return {
          success: false,
          error: 'File size must be less than 5MB'
        };
      }

      // Create filename using asset ID (will replace existing photo)
      const fileExtension = file.name.split('.').pop();
      const fileName = `assets/${assetId}.${fileExtension}`;
      
      // Upload file directly - Firebase Storage will automatically overwrite if file exists
      const storageRef = ref(this.storage, fileName);
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          assetId: assetId,
          companyId: companyId,
          uploadedAt: Date.now().toString(),
          originalName: file.name
        }
      });

      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        success: true,
        url: downloadURL
      };
    } catch (error) {
      console.error('Error uploading photo:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async uploadAssetPhoto(file: File, assetId: string): Promise<PhotoUploadResult> {
    // Alias for backward compatibility
    return this.uploadPhoto(file, assetId, 'default');
  }

  async deleteAssetPhoto(assetId: string, companyId: string = 'default', photoUrl?: string): Promise<PhotoUploadResult> {
    try {
      // If we have a photo URL, try to extract the path and delete it directly
      if (photoUrl) {
        try {
          // Extract the file path from the URL
          const url = new URL(photoUrl);
          const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
          if (pathMatch) {
            const filePath = decodeURIComponent(pathMatch[1]);
            const storageRef = ref(this.storage, filePath);
            await deleteObject(storageRef);
            console.log('Successfully deleted photo by URL path:', filePath);
            return {
              success: true
            };
          }
        } catch (error) {
          console.warn('Failed to delete photo by URL, falling back to extension method:', error);
        }
      }

      // Fallback: Try to find and delete the photo file by trying common extensions
      // Since we don't know the exact extension, we'll try common ones
      const commonExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      for (const ext of commonExtensions) {
        try {
          const fileName = `assets/${assetId}.${ext}`;
          const storageRef = ref(this.storage, fileName);
          await deleteObject(storageRef);
          console.log('Successfully deleted photo by extension:', fileName);
          return {
            success: true
          };
        } catch (error) {
          // Continue to next extension if this one doesn't exist
          continue;
        }
      }
      
      return {
        success: true,
        error: 'No photo found to delete'
      };
    } catch (error) {
      console.error('Error deleting photo:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async deletePhotoByPath(filePath: string): Promise<PhotoUploadResult> {
    try {
      const storageRef = ref(this.storage, filePath);
      await deleteObject(storageRef);
      
      return {
        success: true
      };
    } catch (error) {
      console.error('Error deleting photo:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}
